import { Flex } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectItem } from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { Tooltip } from '~/components/ui/tooltip'

import { PortaledDropdownSelect } from './PortaledDropdownSelect'
import { StatusPill } from './StatusPill'

type FollowUpStatus = {
  value: string
  maskBg: string
  textColor: string
}

const STATUS_OPTIONS: FollowUpStatus[] = [
  {
    value: 'Open',
    maskBg: 'gray.subtle',
    textColor: 'gray.fg',
  },
  {
    value: 'Returned - Call back',
    maskBg: 'green.subtle',
    textColor: 'green.fg',
  },
  {
    value: 'Returned - SMS',
    maskBg: 'blue.subtle',
    textColor: 'blue.fg',
  },
  {
    value: 'Returned - Voicemail',
    maskBg: 'blue.subtle',
    textColor: 'blue.fg',
  },
  {
    value: 'Returned - Email',
    maskBg: 'blue.subtle',
    textColor: 'blue.fg',
  },
  {
    value: 'Returned - No answer',
    maskBg: 'orange.subtle',
    textColor: 'orange.fg',
  },
  {
    value: 'No follow up required',
    maskBg: 'cyan.subtle',
    textColor: 'cyan.fg',
  },
]

const getStatusLabel = (value: string, t: (key: string) => string): string => {
  const statusMap: Record<string, string> = {
    Open: t('missedCalls.followUpStatus.open'),
    'Returned - Call back': t('missedCalls.followUpStatus.returnedCallBack'),
    'Returned - SMS': t('missedCalls.followUpStatus.returnedSMS'),
    'Returned - Voicemail': t('missedCalls.followUpStatus.returnedVoicemail'),
    'Returned - Email': t('missedCalls.followUpStatus.returnedEmail'),
    'Returned - No answer': t('missedCalls.followUpStatus.returnedNoAnswer'),
    'No follow up required': t('missedCalls.followUpStatus.noFollowUpRequired'),
  }
  return statusMap[value] || value
}

type FollowUpStatusDropdownProps = {
  value?: string
  onChange?: (newValue: string) => void
  isDisabled?: boolean
}

export const FollowUpStatusDropdown = ({
  value,
  onChange,
  isDisabled,
}: FollowUpStatusDropdownProps) => {
  const { t } = useTranslation()
  const items: SelectItem<string>[] = useMemo(() => {
    return STATUS_OPTIONS.map(({ value, maskBg, textColor }) => {
      const translatedLabel = getStatusLabel(value, t)
      const pill = (
        <StatusPill text={translatedLabel} bg={maskBg} color={textColor} />
      )
      return {
        value,
        selectedLabel: pill,
        content: (
          <MenuListItem>
            <Flex py="0.375rem">
              <StatusPill
                text={translatedLabel}
                bg={maskBg}
                color={textColor}
              />
            </Flex>
          </MenuListItem>
        ),
        searchLabel: translatedLabel,
      }
    })
  }, [t])

  const handleSelect = (newValue: string) => {
    if (isDisabled) {
      return
    }
    onChange?.(newValue)
  }

  const resolvedValue = value ?? 'Open'

  return (
    <Tooltip
      content={t('missedCalls.followUpStatus.assignUserFirst')}
      disabled={!isDisabled}
      showArrow
      positioning={{ placement: 'top' }}
    >
      <Flex>
        <PortaledDropdownSelect<string>
          placeholder={t('form.placeholder.selectStatus')}
          items={items}
          selectedValue={resolvedValue}
          onSelect={handleSelect}
          isDisabled={isDisabled}
          menuOptions={{
            width: '18rem',
          }}
        />
      </Flex>
    </Tooltip>
  )
}

export default FollowUpStatusDropdown
